import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PackageInfo {
  id: string;
  name: string;
  monthly_price_cents: number;
  features: string[];
  max_messages_monthly: number;
  max_integrations: number;
  default_autonomy_mode: string;
}

interface SubscriptionInfo {
  id: string;
  status: string;
  effective_from: string;
  effective_until: string | null;
  has_stripe_subscription: boolean;
}

export interface Entitlement {
  key: string;
  value: string;
  type: 'boolean' | 'integer' | 'enum';
}

interface UsageInfo {
  period: string;
  meters: Record<string, number>;
}

interface UpgradeOption {
  id: string;
  name: string;
  monthly_price_cents: number;
}

export interface SubscriptionSummary {
  current_package: PackageInfo | null;
  subscription: SubscriptionInfo | null;
  billing_status: string;
  entitlements: Entitlement[];
  usage: UsageInfo;
  upgrade: {
    eligible: boolean;
    options: UpgradeOption[];
  };
  billing_portal: {
    eligible: boolean;
  };
}

type BlockReason =
  | { type: 'no_subscription' }
  | { type: 'package_block'; current_package: string | null; required_entitlement: string }
  | { type: 'quota_block'; meter: string; current: number; limit: number }
  | { type: 'config_block'; reason: string };

interface SubscriptionContextValue {
  loading: boolean;
  summary: SubscriptionSummary | null;
  /** Check if an entitlement key is granted by the current package */
  hasEntitlement: (key: string) => boolean;
  /** Get the limit value for an entitlement (returns null if not found) */
  getEntitlementValue: (key: string) => string | null;
  /** Check if a usage meter is within its quota limit */
  isWithinQuota: (meterType: string, limitEntitlementKey: string) => boolean;
  /** Get the block reason for a given entitlement key (null if allowed) */
  getBlockReason: (entitlementKey: string) => BlockReason | null;
  /** Re-fetch subscription data */
  refresh: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

interface SubscriptionSummaryResponse {
  success: boolean;
  current_package: PackageInfo | null;
  subscription: SubscriptionInfo | null;
  billing_status: string;
  entitlements: Entitlement[];
  usage: UsageInfo;
  upgrade: { eligible: boolean; options: UpgradeOption[] };
  billing_portal: { eligible: boolean };
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user?.userId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    try {
      const res = await get<SubscriptionSummaryResponse>('/api/billing/subscription-summary');
      if (res.success) {
        setSummary({
          current_package: res.current_package,
          subscription: res.subscription,
          billing_status: res.billing_status,
          entitlements: res.entitlements || [],
          usage: res.usage || { period: '', meters: {} },
          upgrade: res.upgrade || { eligible: false, options: [] },
          billing_portal: res.billing_portal || { eligible: false },
        });
      }
    } catch {
      // Keep existing data on transient error; deny during initial load
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const hasEntitlement = useCallback((key: string): boolean => {
    if (loading || !summary) return false;
    const ent = summary.entitlements.find((e) => e.key === key);
    if (!ent) return false;
    if (ent.type === 'boolean') return ent.value === 'true';
    // For integer/enum types, existence means granted
    return true;
  }, [loading, summary]);

  const getEntitlementValue = useCallback((key: string): string | null => {
    if (!summary) return null;
    const ent = summary.entitlements.find((e) => e.key === key);
    return ent ? ent.value : null;
  }, [summary]);

  const isWithinQuota = useCallback((meterType: string, limitEntitlementKey: string): boolean => {
    if (!summary) return false;
    const ent = summary.entitlements.find((e) => e.key === limitEntitlementKey);
    if (!ent || ent.type !== 'integer') return true; // no limit = unlimited
    const limit = parseInt(ent.value, 10);
    if (isNaN(limit) || limit >= 999999) return true; // effectively unlimited
    const current = summary.usage.meters[meterType] ?? 0;
    return current < limit;
  }, [summary]);

  const getBlockReason = useCallback((entitlementKey: string): BlockReason | null => {
    if (loading) return null; // still loading, don't block yet
    if (!summary || !summary.subscription) {
      return { type: 'no_subscription' };
    }
    const ent = summary.entitlements.find((e) => e.key === entitlementKey);
    if (!ent) {
      return {
        type: 'package_block',
        current_package: summary.current_package?.name ?? null,
        required_entitlement: entitlementKey,
      };
    }
    if (ent.type === 'boolean' && ent.value !== 'true') {
      return {
        type: 'package_block',
        current_package: summary.current_package?.name ?? null,
        required_entitlement: entitlementKey,
      };
    }
    return null; // allowed
  }, [loading, summary]);

  return (
    <SubscriptionContext.Provider value={{ loading, summary, hasEntitlement, getEntitlementValue, isWithinQuota, getBlockReason, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return ctx;
}
